import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuthStore } from '../../store';
import AgentOffers from './AgentOffers';

const STATUS_COLORS = {
  payment_received: { bg: '#eaf4ef', color: '#2d6a4f', label: '✓ Pago confirmado' },
  in_delivery:      { bg: '#fef3e2', color: '#b45309', label: '🚚 En entrega' },
  completed:        { bg: '#f0ebe2', color: '#7a7060', label: '✓ Entregado' },
};

export default function AgentDashboard() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [tab, setTab] = useState('available');
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(false);
  const [delivering, setDelivering] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [profileRes, availRes, txRes] = await Promise.all([
        api.get('/api/v1/agents/my'),
        api.get('/api/v1/agents/orders/available'),
        api.get('/api/v1/transactions/'),
      ]);
      setProfile(profileRes.data);
      setAvailableOrders(Array.isArray(availRes.data) ? availRes.data : []);
      const txs = Array.isArray(txRes.data) ? txRes.data : [];
      setMyOrders(txs.filter(t => ['in_delivery', 'completed'].includes(t.status)));
      setIsOnline(profileRes.data?.is_online || false);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Heartbeat every 60 seconds when online
  useEffect(() => {
    if (!isOnline) return;
    const interval = setInterval(async () => {
      try { await api.post('/api/v1/agents/heartbeat'); } catch { }
    }, 60000);
    return () => clearInterval(interval);
  }, [isOnline]);

  const toggleOnline = async () => {
    try {
      if (!isOnline) {
        await api.post('/api/v1/agents/heartbeat');
        setIsOnline(true);
      } else {
        await api.post('/api/v1/agents/offline');
        setIsOnline(false);
      }
    } catch { }
  };

  const acceptOrder = async (ref) => {
    try {
      await api.post(`/api/v1/agent/deliveries/${ref}/accept`);
      await loadData();
      setTab('active');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al aceptar la orden');
    }
  };

  const [deliverForm, setDeliverForm] = useState(null); // ref de la orden en entrega
  const [otp, setOtp] = useState('');
  const [photo, setPhoto] = useState(null);

  const deliverOrder = async (ref) => {
    if (!otp || otp.length < 6) { alert('Pide al receptor su código OTP de 6 dígitos'); return; }
    if (!photo) { alert('Toma la foto de la entrega'); return; }
    setDelivering(ref);
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 10000 })
      ).catch(() => null);
      const fd = new FormData();
      fd.append('otp', otp);
      fd.append('gps_lat', pos ? pos.coords.latitude : 0);
      fd.append('gps_lng', pos ? pos.coords.longitude : 0);
      fd.append('photo', photo);
      await api.post(`/api/v1/agent/deliveries/${ref}/deliver`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setDeliverForm(null); setOtp(''); setPhoto(null);
      await loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al confirmar entrega');
    } finally { setDelivering(null); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>;

  if (!profile) return (
    <div style={{ maxWidth: 480, margin: '40px auto', textAlign: 'center', padding: 24 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🤝</div>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 22, marginBottom: 8 }}>No eres agente todavía</h2>
      <p style={{ fontSize: 14, color: '#7a7060', marginBottom: 24 }}>Aplica para convertirte en agente de Guarapo.</p>
      <button onClick={() => navigate('/app/agente')} style={{ padding: '13px 24px', borderRadius: 10, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        Aplicar como agente →
      </button>
    </div>
  );

  if (profile.status === 'pending') return (
    <div style={{ maxWidth: 480, margin: '40px auto', textAlign: 'center', padding: 24 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 22, marginBottom: 8 }}>Solicitud en revisión</h2>
      <p style={{ fontSize: 14, color: '#7a7060', marginBottom: 16 }}>
        Tu código de agente es <b style={{ fontFamily: 'monospace', color: '#c94a1e' }}>{profile.code}</b>.
      </p>
      <p style={{ fontSize: 13, color: '#7a7060' }}>Recibirás una respuesta en 24-48 horas.</p>
    </div>
  );

  if (profile.status === 'rejected') return (
    <div style={{ maxWidth: 480, margin: '40px auto', textAlign: 'center', padding: 24 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
      <h2 style={{ fontFamily: 'Georgia,serif', fontSize: 22, marginBottom: 8 }}>Solicitud no aprobada</h2>
      <p style={{ fontSize: 14, color: '#7a7060' }}>{profile.rejection_reason || 'Contacta soporte para más información.'}</p>
    </div>
  );

  const activeOrders = myOrders.filter(t => t.status === 'in_delivery');
  const completedOrders = myOrders.filter(t => t.status === 'completed');

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 24, margin: '0 0 4px' }}>Panel de agente</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#c94a1e' }}>{profile.code}</span>
            <span style={{ fontSize: 12, color: '#7a7060' }}>· {profile.city || profile.province || profile.country || '—'}</span>
          </div>
        </div>
        <button onClick={toggleOnline} style={{ padding: '8px 16px', borderRadius: 20, border: 'none', background: isOnline ? '#eaf4ef' : '#f0ebe2', color: isOnline ? '#2d6a4f' : '#7a7060', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: isOnline ? '#2d6a4f' : '#b0a090' }} />
          {isOnline ? 'Online' : 'Offline'}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Disponibles', value: availableOrders.length, color: '#2d6a4f', bg: '#eaf4ef' },
          { label: 'En entrega', value: activeOrders.length, color: '#b45309', bg: '#fef3e2' },
          { label: 'Entregadas', value: profile.total_delivered || 0, color: '#7a7060', bg: '#f0ebe2' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 28, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: '#f0ebe2', borderRadius: 10, padding: 4, marginBottom: 16 }}>
        {[['available', '📋 Disponibles'], ['active', '🚚 Activas'], ['offers', '🏷️ Anuncios'], ['history', '✓ Historial']].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: '8px 4px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: tab === t ? '#fff' : 'transparent', color: tab === t ? '#0f0d0b' : '#7a7060' }}>
            {l}
          </button>
        ))}
      </div>

      {/* Available orders */}
      {tab === 'available' && (
        availableOrders.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', border: '1px solid #e2dbd0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 15, color: '#7a7060' }}>Sin órdenes disponibles</div>
            <div style={{ fontSize: 12, color: '#b0a090', marginTop: 6 }}>
              {isOnline ? 'Espera nuevas órdenes...' : 'Pon tu estado en Online para recibir órdenes'}
            </div>
          </div>
        ) : availableOrders.map((order, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 12, padding: '18px 20px', marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{order.recipient_name}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#7a7060' }}>{order.recipient_phone}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: 24, fontWeight: 700, color: '#c94a1e' }}>${order.net_usd || order.amount_usd}</div>
                <div style={{ fontSize: 11, color: '#7a7060' }}>{order.delivery_method?.replace('_', ' ')}</div>
              </div>
            </div>
            <button onClick={() => acceptOrder(order.ref)} style={{ width: '100%', padding: '11px', borderRadius: 8, border: 'none', background: '#c94a1e', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Aceptar orden →
            </button>
          </div>
        ))
      )}

      {/* Active orders */}
      {tab === 'active' && (
        activeOrders.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', border: '1px solid #e2dbd0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
            <div style={{ fontSize: 15, color: '#7a7060' }}>Sin órdenes activas</div>
          </div>
        ) : activeOrders.map((order, i) => (
          <div key={i} style={{ background: '#fff', border: '2px solid #b45309', borderRadius: 12, padding: '18px 20px', marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#c94a1e', marginBottom: 4 }}>{order.ref}</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{order.recipient_name}</div>
                <div style={{ fontSize: 13, color: '#7a7060', marginTop: 2 }}>{order.recipient_phone}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: 28, color: '#b45309' }}>${order.net_usd || order.amount_usd}</div>
                <div style={{ fontSize: 11, color: '#7a7060' }}>{order.delivery_method?.replace('_', ' ')}</div>
              </div>
            </div>
            {deliverForm !== order.ref ? (
              <button onClick={() => { setDeliverForm(order.ref); setOtp(''); setPhoto(null); }}
                style={{ width: '100%', padding: '13px', borderRadius: 8, border: 'none', background: '#2d6a4f', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                ✓ Entregar — registrar prueba
              </button>
            ) : (
              <div style={{ background: '#f6f1e9', borderRadius: 10, padding: 14 }}>
                <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>
                  Código OTP del receptor
                </label>
                <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} maxLength={6}
                  placeholder="000000" inputMode="numeric"
                  style={{ width: '100%', padding: '12px', borderRadius: 8, border: '1px solid #e2dbd0', fontSize: 22, fontWeight: 700, textAlign: 'center', letterSpacing: 8, boxSizing: 'border-box', marginBottom: 10, fontFamily: 'monospace' }} />
                <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>
                  Foto de la entrega
                </label>
                <input type="file" accept="image/*" capture="environment"
                  onChange={e => setPhoto(e.target.files?.[0] || null)}
                  style={{ width: '100%', marginBottom: 12, fontSize: 13 }} />
                {photo && <div style={{ fontSize: 12, color: '#2d6a4f', marginBottom: 10 }}>✓ {photo.name}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setDeliverForm(null)}
                    style={{ flex: 1, padding: '12px', borderRadius: 8, border: '1px solid #e2dbd0', background: '#fff', color: '#4a4035', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Cancelar
                  </button>
                  <button onClick={() => deliverOrder(order.ref)} disabled={delivering === order.ref}
                    style={{ flex: 2, padding: '12px', borderRadius: 8, border: 'none', background: delivering === order.ref ? '#e2dbd0' : '#2d6a4f', color: '#fff', fontSize: 13, fontWeight: 700, cursor: delivering === order.ref ? 'not-allowed' : 'pointer' }}>
                    {delivering === order.ref ? 'Confirmando...' : '✓ Confirmar entrega'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}

      {/* History */}
      {tab === 'offers' && (
        <AgentOffers isOnline={isOnline} onToggleOnline={toggleOnline} />
      )}

      {tab === 'history' && (
        completedOrders.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 14, padding: 40, textAlign: 'center', border: '1px solid #e2dbd0' }}>
            <div style={{ fontSize: 15, color: '#7a7060' }}>Sin entregas completadas todavía</div>
          </div>
        ) : completedOrders.map((order, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 12, padding: '14px 18px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#c94a1e' }}>{order.ref}</div>
              <div style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>{order.recipient_name}</div>
              <div style={{ fontSize: 11, color: '#7a7060', marginTop: 2 }}>
                {order.completed_at ? new Date(order.completed_at).toLocaleDateString('es-ES') : '—'}
              </div>
            </div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, color: '#2d6a4f', fontWeight: 700 }}>
              ${order.net_usd || order.amount_usd}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
